import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { navigationItems, NavItem } from '../../router/navigationConfig';

interface Destination {
  label: string;
  path: string;
  group: string;
  icon?: React.ReactElement;
}

// Aplana navigationItems (incluye submenús) + rutas extra para la paleta.
const buildDestinations = (): Destination[] => {
  const dest: Destination[] = [];
  navigationItems.forEach((item: NavItem) => {
    if (item.submenu) {
      item.submenu.forEach((sub) =>
        dest.push({ label: sub.label, path: sub.path, group: item.label, icon: sub.icon })
      );
    } else {
      dest.push({ label: item.label, path: item.path, group: 'General', icon: item.icon });
    }
  });
  dest.push({ label: 'Settings', path: '/settings', group: 'General', icon: <SettingOutlined /> });
  return dest;
};

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [query, setQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);

  const destinations = React.useMemo(buildDestinations, []);

  const results = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return destinations;
    return destinations.filter(
      (d) => d.label.toLowerCase().includes(q) || d.group.toLowerCase().includes(q)
    );
  }, [destinations, query]);

  // Al abrir: limpiar y enfocar.
  React.useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      // Enfocar en el siguiente frame para que el input ya esté montado.
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  // Mantener el índice activo dentro del rango al filtrar.
  React.useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const go = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = results[activeIndex];
      if (target) go(target.path);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xl bg-white dark:bg-dark-bg rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 border-b border-gray-200 dark:border-gray-700">
          <SearchOutlined className="text-gray-400 text-lg" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar o ir a…"
            className="flex-1 bg-transparent py-4 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
          />
          <kbd className="hidden sm:inline text-[10px] font-medium text-gray-400 border border-gray-300 dark:border-gray-600 rounded px-1.5 py-0.5">
            ESC
          </kbd>
        </div>

        <div className="max-h-80 overflow-y-auto py-2">
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-400">Sin resultados</div>
          ) : (
            results.map((d, index) => (
              <button
                key={`${d.group}-${d.path}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => go(d.path)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                  index === activeIndex
                    ? 'bg-gray-100 dark:bg-gray-800 text-black dark:text-white'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <span className="text-gray-400">{d.icon}</span>
                <span className="font-medium">{d.label}</span>
                <span className="ml-auto text-xs text-gray-400">{d.group}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
